import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { evaluateLicenseUiState, shouldShowTrialContinuationGate } from '../../utils/licenseUi';
import TrialContinuationGate from './TrialContinuationGate';

/**
 * Guard central: muestra TrialContinuationGate cuando phase === trial_expired.
 * No afecta demo_expired / active / grace / blocked (otros flujos).
 */
const TrialLicenseGate = ({ children }) => {
  const { orgId } = useAuth();
  const [lic, setLic] = useState(null);
  const [ui, setUi] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!orgId) {
      setLic(null);
      setUi(null);
      setLoading(false);
      return;
    }
    try {
      let data = null;
      const r1 = await getDoc(doc(db, `companies/${orgId}/config/license`));
      if (r1.exists()) data = r1.data();
      if (!data) {
        const r2 = await getDoc(doc(db, 'licenses', orgId));
        if (r2.exists()) data = r2.data();
      }
      const license = data || {};
      setLic(license);
      setUi(evaluateLicenseUiState(license));
    } catch {
      setLic({});
      setUi(evaluateLicenseUiState({}));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onReload = () => reload();
    window.addEventListener('nexo-license-reload', onReload);
    window.addEventListener('license:ok', onReload);
    return () => {
      window.removeEventListener('nexo-license-reload', onReload);
      window.removeEventListener('license:ok', onReload);
    };
  }, [reload]);

  // Reevaluación periódica (trial que vence mid-session).
  useEffect(() => {
    if (!orgId) return undefined;
    const t = setInterval(() => {
      reload();
    }, 60000);
    return () => clearInterval(t);
  }, [orgId, reload]);

  if (loading) {
    return children;
  }

  if (shouldShowTrialContinuationGate(ui)) {
    return <TrialContinuationGate lic={lic} onLicenseReload={reload} />;
  }

  return children;
};

export default TrialLicenseGate;
